/**
 * Turn any error-shaped value into one human-readable string.
 *
 * The guard this exists for: a Zod `flatten()` result — an object with the keys
 * `{ formErrors, fieldErrors }` — reaches a string-only sink (a `toast.error`,
 * a thrown `Error`, or a JSX child) and React refuses the object as a child,
 * which throws minified error #31 and unmounts the page. More than 200 API
 * routes return `parsed.error.flatten()` on a 400, so every client read of a
 * response `error` field must pass through this extractor, never straight into
 * a sink.
 *
 * Pure module — no server or React imports — so both client components and the
 * server helper in `lib/api-error.ts` can share the flatten formatter.
 */

/**
 * Collect the readable messages from a Zod `flatten()` result, in order:
 * form-level errors first, then each field error prefixed with its field name.
 * Every field is re-checked at runtime, so an unrelated object yields `[]`.
 */
export function flattenedZodMessages(value: {
  formErrors?: unknown;
  fieldErrors?: unknown;
}): string[] {
  const messages: string[] = [];

  if (Array.isArray(value.formErrors)) {
    for (const message of value.formErrors) {
      if (typeof message === "string" && message.trim()) messages.push(message);
    }
  }

  const fieldErrors = value.fieldErrors;
  if (fieldErrors && typeof fieldErrors === "object") {
    for (const [field, errors] of Object.entries(fieldErrors)) {
      if (!Array.isArray(errors)) continue;
      for (const message of errors) {
        if (typeof message === "string" && message.trim()) {
          messages.push(`${field}: ${message}`);
        }
      }
    }
  }

  return messages;
}

/**
 * Reduce any value to a string safe to show a user, falling back to `fallback`
 * for a non-string, a blank string, or an object that carries no readable
 * message (including a non-Zod object).
 */
export function getErrorMessage(value: unknown, fallback: string): string {
  if (typeof value === "string") return value.trim() ? value : fallback;
  if (value instanceof Error) return value.message.trim() ? value.message : fallback;
  if (value && typeof value === "object") {
    const messages = flattenedZodMessages(value as { formErrors?: unknown; fieldErrors?: unknown });
    return messages.length ? messages.join(". ") : fallback;
  }
  return fallback;
}
